/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import {
  FormControlLabel,
  Grid,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Radio,
  Typography,
} from '@mui/material';
import type { PopoverOrigin, SxProps, Theme } from '@mui/material';
import React from 'react';
import { Options } from '@opengeoweb/theme';
import { CustomIconButton } from '../CustomIconButton';
import type { Variant } from '../CustomIconButton';

type MenuPosition = 'left' | 'right' | 'bottom' | 'top';

export const getPosition = (
  menuPosition: MenuPosition,
): { anchorOrigin: PopoverOrigin; transformOrigin: PopoverOrigin } => {
  switch (menuPosition) {
    case 'bottom':
      return {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
      };
    case 'top':
      return {
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
        transformOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      };

    // eslint-disable-next-line default-case-last
    default:
    case 'left':
      return {
        anchorOrigin: {
          vertical: 'center',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'center',
          horizontal: 'right',
        },
      };

    case 'right':
      return {
        anchorOrigin: {
          vertical: 'center',
          horizontal: 'right',
        },
        transformOrigin: {
          vertical: 'center',
          horizontal: 'left',
        },
      };
  }
};

export interface MenuItemType {
  text: string;
  action: (event: React.MouseEvent) => void;
  icon?: React.ReactNode;
  isDisabled?: boolean;
  isSelected?: boolean;
  controlType?: 'radio';
}

export interface ToggleMenuButtonRenderProps {
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  isOpen: boolean;
}

interface ToggleMenuProps {
  buttonTestId?: string;
  menuPosition?: MenuPosition;
  menuItems?: MenuItemType[];
  menuTitle?: string;
  secondaryMenuItems?: MenuItemType[];
  secondaryMenuTitle?: string;
  children?: React.ReactNode;
  buttonSx?: SxProps<Theme>;
  /**
   * Renders a custom toggle button instead of the default CustomIconButton.
   * The given onClick must be wired to the rendered button so the menu can
   * anchor itself to it.
   */
  renderButton?: (props: ToggleMenuButtonRenderProps) => React.ReactNode;
  tooltipTitle?: string;
  variant?: Variant;
  isDefaultOpen?: boolean;
  buttonIcon?: React.ReactNode;
  isDisabled?: boolean;
}

export const ToggleMenu: React.FC<ToggleMenuProps> = ({
  buttonTestId = 'toggleMenuButton',
  menuPosition = 'left',
  menuItems,
  children,
  buttonSx,
  renderButton,
  tooltipTitle = '',
  menuTitle = '',
  secondaryMenuTitle = '',
  secondaryMenuItems,
  variant = 'tool',
  isDefaultOpen = false,
  buttonIcon = <Options />,
  isDisabled = false,
}: ToggleMenuProps) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const { anchorOrigin, transformOrigin } = getPosition(menuPosition);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const renderMenuItems = (items: MenuItemType[]): React.ReactNode =>
    items.map((menuItem) => {
      const { text, icon, action, isDisabled, isSelected, controlType } =
        menuItem;
      const isRadioControl = controlType === 'radio';

      return (
        <MenuItem
          disabled={isDisabled}
          key={text}
          onClick={(event: React.MouseEvent): void => {
            action(event);
            handleClose(event);
          }}
          selected={!isRadioControl && isSelected}
        >
          <Grid
            container
            size="grow"
            spacing={isRadioControl ? 0 : 2}
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Grid sx={{ display: 'flex', alignItems: 'center' }}>
              {isRadioControl ? (
                <FormControlLabel
                  control={
                    <Radio
                      checked={Boolean(isSelected)}
                      disableRipple
                      size="small"
                      tabIndex={-1}
                    />
                  }
                  label={text}
                  sx={{
                    margin: 0,
                    pointerEvents: 'none',
                    width: '100%',
                  }}
                />
              ) : (
                <ListItemText>{text}</ListItemText>
              )}
            </Grid>
            {icon ? (
              <Grid
                sx={{
                  alignItems: 'flex-end',
                }}
              >
                <ListItemIcon
                  sx={{ '&.MuiListItemIcon-root': { minWidth: '24px' } }}
                >
                  {icon}
                </ListItemIcon>
              </Grid>
            ) : null}
          </Grid>
        </MenuItem>
      );
    });

  React.useEffect(() => {
    if (isDefaultOpen) {
      setAnchorEl(document.getElementById(buttonTestId));
    }
  }, [isDefaultOpen, buttonTestId]);

  return (
    <>
      {renderButton ? (
        renderButton({ onClick: handleClick, isOpen })
      ) : (
        <CustomIconButton
          data-testid={buttonTestId}
          disabled={isDisabled}
          id={buttonTestId}
          isSelected={isOpen}
          onClick={handleClick}
          sx={buttonSx}
          tooltipProps={{
            placement: 'top',
            title: tooltipTitle,
          }}
          variant={variant}
        >
          {buttonIcon}
        </CustomIconButton>
      )}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        onClose={(
          event: React.MouseEvent,
          reason: 'backdropClick' | 'escapeKeyDown' | 'tabKeyDown',
        ): void => {
          if (reason === 'tabKeyDown') {
            return;
          }
          handleClose(event);
        }}
        open={isOpen}
        slotProps={{
          backdrop: {
            style: {
              opacity: 0,
              transition: 'none',
            },
          },
          list: {
            ...(children && {
              style: {
                padding: '0px',
              },
            }),
          },
        }}
        transformOrigin={transformOrigin}
      >
        {menuTitle ? (
          <ListSubheader data-testid="toggleMenuTitle">
            <Typography sx={{ fontSize: '12px' }}>{menuTitle}</Typography>
          </ListSubheader>
        ) : null}
        {menuItems?.length ? (
          renderMenuItems(menuItems)
        ) : (
          <div /> // Needed to make the menu work without any menuItems
        )}
        {secondaryMenuTitle ? (
          <ListSubheader data-testid="toggleMenuSecondaryTitle">
            <Typography sx={{ fontSize: '12px' }}>
              {secondaryMenuTitle}
            </Typography>
          </ListSubheader>
        ) : null}
        {secondaryMenuItems?.length ? (
          renderMenuItems(secondaryMenuItems)
        ) : (
          <div /> // Needed to make the menu work without any menuItems
        )}
        {children}
      </Menu>
    </>
  );
};
